/**
 * @author      Shivani Mehra
 * @date        Aug, 2024
 * @copyright   www.ibirdsservices.com
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Row, Col, Card, Dropdown, Image, Badge } from "react-bootstrap";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import * as constants from "../../constants/CONSTANT";
import { toast } from "react-toastify"; //npm i react-toastify --force
import "react-toastify/dist/ReactToastify.css";
import TemplateModal from "./TemplateModal";
import moment from "moment";
import SendFileModal from "./SendFileModal";
import { NameInitialsAvatar } from "react-name-initials-avatar"; // npm install react-name-initials-avatar --force
import jwt_decode from "jwt-decode";
import MessageTemplateModal from "./MessageTemplateModal";
import QuickRepliesTemplateModel from "./QuickRepliesTemplateModel";
import CallModal from "../calls/CallModal";
import CarouselCards from "./CarouselCards"; // Adjust path as needed
import TagAssignmentModal from "./TagAssignmentModal";

const MSG_PAGE_SIZE = 50;

function mergeLatestHistoryPage(prev, freshPage) {
  if (!freshPage?.length) return prev;
  const freshIds = new Set(freshPage.map((m) => m.message_history_id ?? m.id));
  const oldestFresh = new Date(freshPage[0].createddate).getTime();
  const retained = prev.filter((m) => {
    const id = m.message_history_id ?? m.id;
    const t = new Date(m.createddate).getTime();
    return t < oldestFresh && !freshIds.has(id);
  });
  return [...retained, ...freshPage].sort(
    (a, b) => new Date(a.createddate) - new Date(b.createddate),
  );
}

const WhatsAppChat = ({
  show,
  onHide,
  userDetail,
  socket,
  filterData,
  selectedWhatsAppSetting,
  onArchiveListChange,
  onClearHistory,
}) => {
  const [userInfo, setUserInfo] = useState(
    jwt_decode(sessionStorage.getItem("token")),
  );
  const [isArchiving, setIsArchiving] = useState(false);
  const [isArchived, setIsArchived] = useState(
    userDetail?.is_archived || false,
  );

  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [receivedMessage, setReceivedMessage] = useState();
  const [showHideModal, setShowHideModal] = useState(false);
  const [showHideBulkModal, setShowHideBulkModal] = useState(false);
  const [contactData, setContactData] = useState();
  const [groupData, setGroupData] = useState();
  const chatWindowRef = useRef(null);
  const [fileModalShowHide, setFileModalShowHide] = useState(false);
  const [showHideQuickModal, setShowHideQuickModal] = useState(false);
  const hasQuickModule =
    userInfo?.modules?.some((module) => module.url === "interactive_message") ||
    false;
  const pdfIcon = "/user_images/pdf-icon-png.jpg";
  const docxIcon = "/user_images/doc-icon.png";
  const xlsIcon = "/user_images/xls-icon.png";
  const mapIcon = "/user_images/google-maps.jpg";
  const textIcon = "/user_images/text-file.jpg";
  const [bgColors, setBgColors] = useState([
    "#d3761f",
    "#00ad5b",
    "#debf31",
    "#239dd1",
    "#b67eb1",
    "#d3761f",
    "#de242f",
  ]);
  const [showCallModal, setShowCallModal] = useState(false);
  const [brokenImages, setBrokenImages] = useState([]);
  const [carouselStates, setCarouselStates] = useState({});
  const [showTagModal, setShowTagModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyNextOffset, setHistoryNextOffset] = useState(0);
  const [loadingHistoryInitial, setLoadingHistoryInitial] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const prependRestoreRef = useRef(null);
  let colIndex = 0;

  const formatPhoneNumber = (number) => {
    const shouldHideNumber =
      userInfo?.userrole !== "ADMIN" &&
      userInfo?.is_whatsapp_number_hidden === true;

    if (shouldHideNumber && number) {
      // Keep last 5 digits visible, mask the rest with X
      const numberString = String(number);
      if (numberString.length <= 5) {
        return numberString; // Show all if number is 5 digits or less
      }
      const visibleDigits = numberString.slice(-5);
      const maskedDigits = "X".repeat(numberString.length - 5);
      return `${maskedDigits}${visibleDigits}`;
    }
    return number;
  };

  useEffect(() => {
    const handleMsg = (data) => {
      // console.log("daata", data);
      setReceivedMessage(data);
    };
    socket?.on("receivedwhatsappmessage", handleMsg);
    socket?.on("outgoingwhatsappmessage", handleMsg);
    return () => {
      socket?.off("receivedwhatsappmessage", handleMsg);
      socket?.off("outgoingwhatsappmessage", handleMsg);
    };
  }, [socket]);

  useLayoutEffect(() => {
    const el = chatWindowRef.current;
    if (!el) return;
    const restore = prependRestoreRef.current;
    if (restore) {
      el.scrollTop = el.scrollHeight - restore.prevHeight + restore.prevTop;
      prependRestoreRef.current = null;
      return;
    }
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  useEffect(() => {
    getMessageHistotyRecords(selectedWhatsAppSetting);
  }, [userDetail.id, userDetail.full_number, selectedWhatsAppSetting]);

  useEffect(() => {
    if (!receivedMessage || !userDetail.full_number) return;
    const from = receivedMessage.from;
    if (from) {
      const incomingDigits = String(from).replace(/\D/g, "");
      const chatDigits = String(userDetail.full_number).replace(/\D/g, "");
      if (incomingDigits && chatDigits && incomingDigits !== chatDigits) {
        return;
      }
    }
    (async () => {
      const result = await WhatsAppAPI.getMsgHistoryRecords(
        userDetail.full_number,
        selectedWhatsAppSetting,
        { limit: MSG_PAGE_SIZE, offset: 0 },
      );
      if (result.success) {
        setMessages((prev) =>
          mergeLatestHistoryPage(prev, result.records || []),
        );
      }
    })();
  }, [receivedMessage, userDetail.full_number, selectedWhatsAppSetting]);

  const loadOlderMessages = async () => {
    if (
      !userDetail.full_number ||
      !historyHasMore ||
      loadingOlder ||
      loadingHistoryInitial
    ) {
      return;
    }
    const el = chatWindowRef.current;
    prependRestoreRef.current = {
      prevHeight: el?.scrollHeight ?? 0,
      prevTop: el?.scrollTop ?? 0,
    };
    setLoadingOlder(true);
    try {
      const result = await WhatsAppAPI.getMsgHistoryRecords(
        userDetail.full_number,
        selectedWhatsAppSetting,
        { limit: MSG_PAGE_SIZE, offset: historyNextOffset },
      );
      if (result.success && result.records?.length) {
        setMessages((prev) => {
          const merged = [...result.records, ...prev];
          const byId = new Map();
          for (const m of merged) {
            const id = m.message_history_id ?? m.id;
            if (id != null) byId.set(id, m);
          }
          return Array.from(byId.values()).sort(
            (a, b) => new Date(a.createddate) - new Date(b.createddate),
          );
        });
        setHistoryHasMore(result.hasMore ?? false);
        setHistoryNextOffset((o) => o + result.records.length);
      } else {
        prependRestoreRef.current = null;
        setHistoryHasMore(false);
      }
    } catch {
      prependRestoreRef.current = null;
    } finally {
      setLoadingOlder(false);
    }
  };

  const handleChatScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop <= 80) {
      loadOlderMessages();
    }
  };

  const getMessageHistotyRecords = async (selectedWhatsAppSetting) => {
    if (userDetail.id && userDetail.full_number) {
      setLoadingHistoryInitial(true);
      setHistoryNextOffset(0);
      setHistoryHasMore(true);
      try {
        const result = await WhatsAppAPI.getMsgHistoryRecords(
          userDetail.full_number,
          selectedWhatsAppSetting,
          { limit: MSG_PAGE_SIZE, offset: 0 },
        );
        if (result.success) {
          const recs = result.records || [];
          setMessages(recs);
          setHistoryHasMore(result.hasMore ?? false);
          setHistoryNextOffset(recs.length);
        } else {
          setMessages([]);
          setHistoryHasMore(false);
          setHistoryNextOffset(0);
        }
      } finally {
        setLoadingHistoryInitial(false);
      }
    } else if (userDetail.id && !userDetail.full_number) {
      const result = await WhatsAppAPI.getGroupHistoryRecords(
        userDetail.id,
        selectedWhatsAppSetting,
      );
      if (result.success) {
        setMessages(result?.records);
      } else {
        setMessages([]);
      }
      setHistoryHasMore(false);
      setHistoryNextOffset(0);
    } else {
      setMessages([]);
      setHistoryHasMore(false);
      setHistoryNextOffset(0);
    }
  };

  // send whatsapp template message
  const sendTemplateMessage = async () => {
    if (userDetail?.blocked) {
      toast.error("Cannot send template to a blocked lead.");
      return;
    }
    if (userDetail.id && userDetail.full_number) {
      setShowHideModal(true);
      setContactData(userDetail);
    } else {
      toast.error("Error: WhatsApp number is required.");
    }
  };

  const sendBulkTemplateMessage = () => {
    if (userDetail.id && !userDetail.full_number) {
      setShowHideBulkModal(true);
      setGroupData([userDetail]);
    } else {
      toast.error("Error: WhatsApp number is required.");
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      // event.preventDefault();
      handleSubmit();
    }
  };

  const handleSubmit = async (event) => {
    if (userDetail?.blocked) {
      toast.error("Cannot send message to a blocked lead.");
      return;
    }
    // event.preventDefault();
    if (userDetail.full_number) {
      if (input.trim()) {
        const singleText = {
          messaging_product: "whatsapp",
          recipient_type: "individual",
          to: userDetail.full_number,
          type: "text",
          text: {
            preview_url: false,
            body: input,
          },
        };
        const result = await WhatsAppAPI.sendSingleWhatsAppTextMessage(
          singleText,
          selectedWhatsAppSetting,
        );
        if (result.error) {
          toast.error(`Error: ${result.error}`);
        } else {
          const messageId = result?.messages[0]?.id;

          const newMessage = {
            parent_id: userDetail.id || null,
            name: userDetail.contactname || "",
            message_template_id: null,
            whatsapp_number: userDetail.full_number,
            message: input,
            status: "Outgoing",
            recordtypename: filterData?.recordType || "",
            file_id: null,
            is_read: true,
            business_number: selectedWhatsAppSetting,
            message_id: messageId,
            interactive_id: null,
          };

          const responce =
            await WhatsAppAPI.insertMsgHistoryRecords(newMessage);

          getMessageHistotyRecords(selectedWhatsAppSetting);
          if (responce) {
            toast.success("Message sent successfully.");
          } else {
            toast.error(responce.message);
          }
          setInput("");
        }
      } else {
        toast.error("Error: Input field required, please enter text here.");
      }
    } else {
      toast.error("WhatsApp number is required.");
    }
  };

  const refreshData = () => {
    getMessageHistotyRecords(selectedWhatsAppSetting);
    setShowHideModal();
    setShowHideBulkModal();
  };
  const refreshImageData = () => {
    getMessageHistotyRecords(selectedWhatsAppSetting);
    setFileModalShowHide();
  };

  //close
  const onClearButton = async () => {
    if (
      window.confirm(
        "Are you sure you want to clear this chat history? This will permanently delete all messages and associated media files."
      )
    ) {
      const result = await WhatsAppAPI.historyDelete(
        userDetail.full_number
          ? userDetail.full_number
          : userDetail.whatsapp_number,
        selectedWhatsAppSetting,
      );
      if (result.success) {
        toast.success("Chat history cleared successfully!");
        if (onClearHistory) {
          onClearHistory();
        }
        if (onHide) {
          onHide();
        }
      } else {
        toast.error("Failed to clear chat history.");
      }
    }
  };

  const onBlockLeadButton = async () => {
    const leadId = userDetail.lead_id || userDetail.id;
    if (!leadId) {
      toast.error("Lead ID not found");
      return;
    }
    if (
      window.confirm(
        "Are you sure you want to block this lead? They will not be able to receive messages."
      )
    ) {
      try {
        const result = await WhatsAppAPI.blockLead(leadId);
        if (result && result.success) {
          toast.success("Lead blocked successfully!");
          if (onClearHistory) {
            onClearHistory();
          }
          if (onHide) {
            onHide();
          }
        } else {
          toast.error(result?.message || "Failed to block lead.");
        }
      } catch (error) {
        console.error("Error blocking lead:", error);
        toast.error("Failed to block lead.");
      }
    }
  };

  const onUploadFile = () => {
    if (userDetail?.blocked) {
      toast.error("Cannot send files to a blocked lead.");
      return;
    }
    if (userDetail.id) {
      setFileModalShowHide(true);
    }
  };

  const handleArchiveToggle = async () => {
    if (!userDetail?.id || isArchiving) return;

    setIsArchiving(true);
    try {
      const newArchiveStatus = !isArchived;
      const leadId = userDetail.lead_id || userDetail.id;

      if (!leadId) {
        toast.error("Lead ID not found");
        return;
      }

      // Use the existing updateLead method with the updated lead data
      const result = await WhatsAppAPI.updateLead({
        id: leadId,
        is_archived: newArchiveStatus,
        business_number: selectedWhatsAppSetting,
      });

      if (result.success) {
        // Update local state
        setIsArchived(newArchiveStatus);

        // Update the userDetail object
        const updatedUserDetail = {
          ...userDetail,
          is_archived: newArchiveStatus,
        };

        // Show success message
        toast.success(
          newArchiveStatus
            ? "Lead archived successfully!"
            : "Lead unarchived successfully!",
        );

        if (onArchiveListChange) {
          onArchiveListChange(leadId, newArchiveStatus);
        }

        // If we're in "All" or "Unread" view and lead is archived,
        // close the chat after a short delay (list row is already patched in parent)
        if (
          newArchiveStatus &&
          (filterData?.recordType === "lead" ||
            filterData?.recordType === "unread")
        ) {
          setTimeout(() => {
            if (onHide) onHide();
          }, 1500);
        }
      } else {
        toast.error(result.message || "Failed to update archive status");
      }
    } catch (error) {
      console.error("Error toggling archive status:", error);
      toast.error("Error updating archive status");
    } finally {
      setIsArchiving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success("Code copied to clipboard!");
      })
      .catch((err) => {
        ////console.log("Error copying text: ", err);
      });
  };

  const renderCarouselCards = (msg) => {
    return <CarouselCards msg={msg} userInfo={userInfo} pdfIcon={pdfIcon} />;
  };

  const getFilePreviewUrl = (msg) => {
    let filename = "";
    if (msg.file_id && msg.title) {
      filename = msg.title;
    } else if (msg.interactive_file_id && msg.interactive_file_title) {
      filename = msg.interactive_file_title;
    } else if (msg.body_text_params?.file_title) {
      filename = msg.body_text_params.file_title;
    } else if (msg.header_body) {
      const hb = msg.header_body;
      if (hb.startsWith("http") && !hb.includes("/public/")) {
        return hb;
      }
      if (hb.includes("/")) {
        filename = hb.split("/").pop();
      } else {
        filename = hb;
      }
    }
    if (!filename) return "";
    return `${constants.API_BASE_URL}/api/whatsapp/files/preview/${userInfo.tenantcode}/${encodeURIComponent(filename)}`;
  };

  const renderFilePreview = (msg) => {
    const isLink = msg.chatmsg && msg.chatmsg.match(/(https?:\/\/[^\s]+)/g);
    const googleMapsUrlRegex =
      /https?:\/\/(www\.)?google\.com\/maps\/.+|https?:\/\/maps\.app\.goo\.gl\/[a-zA-Z0-9]+/g;
    const googleMapsUrlMatch =
      msg.chatmsg && msg.chatmsg.match(googleMapsUrlRegex);
    const fileUrl = getFilePreviewUrl(msg);
    // const fileUrl = msg.file_id
    //   ? `/public/${userInfo.tenantcode}/attachment/${msg.title}`
    //   : msg.interactive_file_id
    //   ? `/public/${userInfo.tenantcode}/attachment/${msg.interactive_file_title}`
    //   : msg.header_body;

    const fileType = msg.interactive_file_id
      ? msg.interactive_file_type?.replace(".", "").toLowerCase()
      : msg.filetype?.replace(".", "").toLowerCase();

    if (msg.ad_id) {
      return (
        <div
          style={{
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            padding: "12px",
            background: "#ffffff",
            marginBottom: "8px",
            maxWidth: "280px",
            boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
          }}
        >
          {/* Ad Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "8px",
              fontSize: "11px",
              color: "#666",
            }}
          >
            <span
              style={{
                backgroundColor: "#25D366",
                color: "white",
                padding: "2px 6px",
                borderRadius: "4px",
                fontSize: "9px",
                fontWeight: "600",
                marginRight: "6px",
              }}
            >
              AD
            </span>
            Sponsored • {msg.ad_platform?.toUpperCase() || "INSTAGRAM"}
          </div>

          {/* Ad Media (Image / Video using ad_file_url + ad_media_type) */}
          {msg.ad_file_url && (
            <div style={{ width: "100%", marginBottom: "8px" }}>
              {msg.ad_media_type === "image" && (
                <img
                  src={msg.ad_file_url}
                  alt="Ad"
                  style={{
                    width: "100%",
                    height: "140px",
                    borderRadius: "6px",
                    objectFit: "cover",
                    backgroundColor: "#f8f9fa",
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              )}

              {msg.ad_media_type === "video" && (
                <video
                  src={msg.ad_file_url}
                  style={{
                    width: "100%",
                    height: "140px",
                    borderRadius: "6px",
                    objectFit: "cover",
                    backgroundColor: "#f8f9fa",
                  }}
                  muted
                  playsInline
                  controls={false}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              )}
            </div>
          )}

          {/* Ad Content */}
          <div style={{ marginBottom: "8px" }}>
            {/* Ad Headline */}
            {msg.ad_headline && (
              <h6
                style={{
                  margin: "0 0 4px 0",
                  fontWeight: "600",
                  fontSize: "13px",
                  lineHeight: "1.3",
                  color: "#1a1a1a",
                }}
              >
                {msg.ad_headline}
              </h6>
            )}

            {/* Ad Body */}
            {msg.ad_body && (
              <p
                style={{
                  margin: "0 0 6px 0",
                  color: "#666",
                  fontSize: "12px",
                  lineHeight: "1.3",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {msg.ad_body}
              </p>
            )}
          </div>

          {/* Ad URL */}
          {msg.ad_url && (
            <a
              href={msg.ad_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: "12px",
                color: "#0086ff",
                textDecoration: "none",
                fontWeight: "500",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              Learn More
              <span style={{ marginLeft: "4px", fontSize: "11px" }}>→</span>
            </a>
          )}

          {/* Client Message Below Ad - if exists */}
          {msg.chatmsg && (
            <div
              style={{
                marginTop: "10px",
                paddingTop: "10px",
                borderTop: "1px solid #f0f0f0",
              }}
            >
              <div
                style={{
                  fontSize: "13px",
                  color: "#333",
                  lineHeight: "1.3",
                  wordWrap: "break-word",
                }}
              >
                {msg.chatmsg}
              </div>
            </div>
          )}
        </div>
      );
    }
    // Handle carousel template rendering
    if (msg.template_type === "carousel" && msg.template_cards) {
      return renderCarouselCards(msg);
    }

    const formatMessage = (
      message,
      bodyTextParams = {},
      campaignBodyTextParams = {},
      contactName = null,
    ) => {
      if (!message) return <span></span>;

      let formattedMessage = message
        .replace(/\*(.*?)\*/g, "<b>$1</b>")
        .replace(/_(.*?)_/g, "<i>$1</i>")
        .replace(/~(.*?)~/g, "<s>$1</s>")
        .replace(/```(.*?)```/gs, "<pre>$1</pre>")
        .replace(/\n+/g, "<br />");

      try {
        let replacements = {
          ...bodyTextParams,
          ...campaignBodyTextParams,
        };

        // Replace any placeholder that has "name" in the key or value
        formattedMessage = formattedMessage.replace(
          /{{(\d+)}}/g,
          (_, index) => {
            const value = replacements[index];
            // Check if the placeholder value contains "name" (case-insensitive)
            if (
              value &&
              typeof value === "string" &&
              value.toLowerCase().includes("name")
            ) {
              return contactName || value;
            }
            return value || `{{${index}}}`;
          },
        );
      } catch (error) {
        console.error("Error parsing example_body_text or params", error);
      }

      return <span dangerouslySetInnerHTML={{ __html: formattedMessage }} />;
    };

    const hasFile = msg.file_id || msg.interactive_file_id || msg.filetype || ["IMAGE", "DOCUMENT", "VIDEO"].includes(msg.header);

    if (!hasFile && googleMapsUrlMatch) {
      const mapUrl = googleMapsUrlMatch[0];
      return (
        <div style={{ position: "relative" }}>
          <a href={mapUrl} target="_blank" rel="noopener noreferrer">
            <img
              src={mapIcon}
              alt="Location Map Preview"
              style={{
                width: "100%",
                height: "200px",
                objectFit: "contain",
                borderRadius: "8px",
              }}
            />
          </a>
        </div>
      );
    } else if (!hasFile && isLink) {
      return (
        <div className="text-break">
          <a href={msg.chatmsg} target="_blank" rel="noopener noreferrer">
            {msg.chatmsg}
          </a>
        </div>
      );
    } else if (!hasFile && msg.chatmsg) {
      return <div>{formatMessage(msg.chatmsg)}</div>;
    } else if (msg.header) {
      return (
        <div>
          {msg.header === "IMAGE" && (
            <a href={fileUrl} target="_blank" rel="noopener noreferrer">
              <Image
                variant="top"
                src={fileUrl}
                thumbnail
                alt="image"
                style={{
                  width: "100%",
                  objectFit: "contain",
                  maxHeight: "20rem",
                }}
              />
            </a>
          )}
          {msg.header === "DOCUMENT" && (
            <a href={fileUrl} target="_blank" rel="noopener noreferrer">
              <img
                src={pdfIcon}
                alt="Pdf Preview"
                style={{
                  width: "100%",
                  height: "200px",
                  objectFit: "contain",
                  borderRadius: "8px",
                  backgroundColor: "white",
                }}
              />
            </a>
          )}
          {msg.header === "VIDEO" && (
            <video width="293" height="170" controls>
              <source src={fileUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          )}
          {msg.message_body && (
            <div className="mt-2 ms-1">
              <p className="fw-bold mb-1 text-capitalize">
                {msg?.header_body && msg.header === "TEXT" && msg?.header_body}
              </p>
              <p className="mb-1">
                {" "}
                {formatMessage(
                  msg.message_body,
                  msg.body_text_params,
                  msg.campaign_body_text_params,
                  userDetail?.contactname,
                )}
              </p>
              <p className="mb-1 text-secondary"> {msg?.footer}</p>
            </div>
          )}
        </div>
      );
    }

    if (msg.message_body) {
      return (
        <div>
          {formatMessage(
            msg.message_body,
            msg.body_text_params,
            msg.campaign_body_text_params,
            userDetail?.contactname,
          )}
          {/* {formatMessage(msg.message_body)} */}
          {msg?.footer}
        </div>
      );
    }
    switch (msg.interactive_type) {
      case "button":
      case "list":
        return (
          <div>
            {/* ✅ Handle file preview if interactive_file_id exists */}
            {msg.interactive_file_id && (
              <div className="my-2">
                {["jpg", "jpeg", "png", "webp"].includes(fileType) && (
                  <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                    <img
                      src={fileUrl}
                      alt="attachment"
                      style={{
                        width: "100%",
                        height: "200px",
                        objectFit: "contain",
                        borderRadius: "8px",
                      }}
                    />
                  </a>
                )}
                {fileType === "pdf" && (
                  <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                    <img
                      src={pdfIcon}
                      alt="PDF"
                      style={{
                        width: "100%",
                        height: "200px",
                        objectFit: "contain",
                        borderRadius: "8px",
                        backgroundColor: "white",
                      }}
                    />
                  </a>
                )}
                {fileType === "mp4" && (
                  <video
                    controls
                    style={{ maxWidth: "100%", maxHeight: "200px" }}
                  >
                    <source src={fileUrl} type="video/mp4" />
                  </video>
                )}
                {/* You can add more types as needed */}
              </div>
            )}
            {msg.body_text && (
              <p className="">
                {formatMessage(
                  msg.body_text,
                  msg.body_text_params,
                  msg.campaign_body_text_params,
                  userDetail?.contactname,
                )}
              </p>
            )}
            {/* Buttons or List options */}
            {msg.interactive_type === "button" && (
              <div className="d-flex flex-column align-items-center">
                <div className="text-center">
                  {/* <div className="d-flex flex-wrap gap-2"> */}
                  {msg.interactive_buttons?.map((btn, idx) => (
                    <Badge
                      key={idx}
                      bg="light"
                      text="primary"
                      className="mb-2"
                      style={{
                        padding: "10px 40px",
                        fontSize: "0.9rem",
                        borderRadius: "0.5rem",
                        display: "block",
                        textAlign: "center",
                      }}
                    >
                      {btn.text}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {msg.interactive_type === "list" &&
              msg.sections?.map((section, sectionIdx) => (
                <div key={sectionIdx} className="mb-3">
                  {section.title && (
                    <div className="fw-bold text-secondary mb-2">
                      {section.title}
                    </div>
                  )}
                  <div className="d-flex flex-column  gap-2">
                    {section.rows.map((row) => (
                      <Badge
                        key={row.id}
                        bg="light"
                        text="primary"
                        className="mb-2"
                        style={{
                          padding: "10px 20px",
                          fontSize: "0.9rem",
                          borderRadius: "0.5rem",
                          display: "block",
                          textAlign: "left",
                        }}
                      >
                        <div className="fw-semibold">{row.title}</div>
                        {row.description && (
                          <small className="text-muted">
                            {row.description}
                          </small>
                        )}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}

            {msg.footer_text && (
              <p className="text-secondary">{msg.footer_text}</p>
            )}
          </div>
        );

        break;
    }

    // const fileUrl = `/public/${userInfo.tenantcode}/attachment/${msg.title}`;
    switch (msg.filetype) {
      case ".jpeg":
      case ".jpg":
      case ".png":
      case ".webp":
      case "jpeg":
      case "jpg":
      case "png":
      case "webp":
        return (
          <div>
            <a href={fileUrl} target="_blank" rel="noopener noreferrer">
              <img
                src={fileUrl}
                alt={msg.title}
                style={{
                  width: "100%",
                  height: "200px",
                  objectFit: "contain",
                  maxWidth: "100%",
                  maxHeight: "200px",
                }}
              />
            </a>
            <br /> {msg?.description}
            {msg.chatmsg && msg.chatmsg.toLowerCase() !== "sent" && (
              <div className="mt-1">{formatMessage(msg.chatmsg)}</div>
            )}
          </div>
        );
      case "pdf":
      case ".pdf":
        return (
          <div style={{ position: "relative" }}>
            <a href={fileUrl} target="_blank" rel="noopener noreferrer">
              <img
                src={pdfIcon}
                alt="Pdf Preview"
                style={{
                  width: "100%",
                  height: "200px",
                  objectFit: "contain",
                  borderRadius: "8px",
                  backgroundColor: "white",
                }}
              />
            </a>
            <div className="mt-2">
              <a
                className="text-break"
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {msg.title}
              </a>
              <br />
              {msg?.description}
              {msg.chatmsg && msg.chatmsg.toLowerCase() !== "sent" && (
                <div className="mt-1">{formatMessage(msg.chatmsg)}</div>
              )}
            </div>
          </div>
        );
      case "aac":
      case "mpeg":
      case "mp3":
      case "amr":
      case "ogg":
      case "opus":
      case ".aac":
      case ".mpeg":
      case ".mp3":
      case ".amr":
      case ".ogg":
      case "ogg":
      case ".opus":
        return (
          <div>
            <audio controls className="audio-player">
              <source src={fileUrl} type={`audio/${msg.filetype}`} />
            </audio>
            {msg.chatmsg && msg.chatmsg.toLowerCase() !== "sent" && (
              <div className="mt-1">{formatMessage(msg.chatmsg)}</div>
            )}
          </div>
        );
      case "ppt":
      case "pptx":
      case "doc":
      case "docx":
      case ".ppt":
      case ".pptx":
      case ".doc":
      case ".docx":
        return (
          <div style={{ position: "relative" }}>
            <a href={fileUrl} target="_blank" rel="noopener noreferrer">
              <img
                src={docxIcon}
                alt={`${msg.filetype.toUpperCase()} Preview`}
                style={{
                  width: "100%",
                  height: "200px",
                  objectFit: "contain",
                  borderRadius: "8px",
                  backgroundColor: "white",
                }}
              />
            </a>
            <div className="mt-2">
              <a
                className="text-break"
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {msg.title}
              </a>
              <br />
              {msg?.description}
              {msg.chatmsg && msg.chatmsg.toLowerCase() !== "sent" && (
                <div className="mt-1">{formatMessage(msg.chatmsg)}</div>
              )}
            </div>
          </div>
        );
      case "xlsx":
      case "xls":
      case ".xlsx":
      case ".xls":
        return (
          <div style={{ position: "relative" }}>
            <a href={fileUrl} target="_blank" rel="noopener noreferrer">
              <img
                src={xlsIcon}
                alt={`${msg.filetype.toUpperCase()} Preview`}
                style={{
                  width: "100%",
                  height: "200px",
                  borderRadius: "8px",
                  objectFit: "contain",
                  backgroundColor: "white",
                }}
              />
            </a>
            <div className="mt-2">
              <a
                className="text-break"
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {msg.title}
              </a>
              <br />
              {msg?.description}
              {msg.chatmsg && msg.chatmsg.toLowerCase() !== "sent" && (
                <div className="mt-1">{formatMessage(msg.chatmsg)}</div>
              )}
            </div>
          </div>
        );
      case "txt":
      case ".txt":
        return (
          <div style={{ position: "relative" }}>
            <a href={fileUrl} target="_blank" rel="noopener noreferrer">
              <img
                src={textIcon}
                alt={`${msg.filetype.toUpperCase()} Preview`}
                style={{
                  width: "100%",
                  height: "200px",
                  borderRadius: "8px",
                  objectFit: "contain",
                  backgroundColor: "white",
                }}
              />
            </a>
            <div className="mt-2">
              <a
                className="text-break"
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {msg.title}
              </a>
              {msg.chatmsg && msg.chatmsg.toLowerCase() !== "sent" && (
                <div className="mt-1">{formatMessage(msg.chatmsg)}</div>
              )}
            </div>
          </div>
        );
      case "mp4":
      case "3gpp":
      case ".mp4":
      case ".3gpp":
        return (
          <div>
            <video controls style={{ maxWidth: "100%", maxHeight: "200px" }}>
              {/* <source src={fileUrl} type={`video/${msg.filetype}`} /> */}
              <source
                src={fileUrl}
                type={`video/${msg.filetype.replace(".", "")}`}
              />
              Your browser does not support the video element.
            </video>
            {msg?.description}
            {msg.chatmsg && msg.chatmsg.toLowerCase() !== "sent" && (
              <div className="mt-1">{formatMessage(msg.chatmsg)}</div>
            )}
          </div>
        );
      default:
        return (
          <div
            style={{ marginTop: "10px", display: "flex", flexDirection: "column" }}
          >
            <a
              className="text-break"
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {msg.title}
            </a>
            {msg.chatmsg && msg.chatmsg.toLowerCase() !== "sent" && (
              <div className="mt-1">{formatMessage(msg.chatmsg)}</div>
            )}
          </div>
        );
    }
  };

  const renderWebsiteAndCall = (msg) => {
    // if (!msg || !Array.isArray(msg.buttons)) return null;
    return (
      <div className="d-flex flex-column align-items-center">
        <div className="text-center">
          {(() => {
            let buttons = [];
            if (Array.isArray(msg.buttons)) {
              buttons = msg.buttons;
            } else if (Array.isArray(msg.components)) {
              const buttonComponent = msg.components.find(
                (c) => c.type === "button",
              );
              if (buttonComponent && Array.isArray(buttonComponent.buttons)) {
                buttons = buttonComponent.buttons;
              }
            }
            return buttons.length > 0
              ? buttons.map((item, index) => {
                if (
                  item.type === "URL" &&
                  item.text !== "Copy code" &&
                  item.text !== "कोड कॉपी करें"
                ) {
                  return (
                    <a
                      key={index}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: "none" }}
                    >
                      <Badge
                        bg="light"
                        text="primary"
                        className="mb-2"
                        style={{
                          padding: "10px 40px",
                          fontSize: "0.9rem",
                          borderRadius: "0.5rem",
                          display: "block",
                          textAlign: "center",
                        }}
                      >
                        <i className="fa fa-external-link me-2"></i>
                        {item.text}
                      </Badge>
                    </a>
                  );
                } else if (item.type === "PHONE_NUMBER") {
                  return (
                    <a
                      key={index}
                      href={`tel:${item.phone_number}`}
                      style={{ textDecoration: "none" }}
                    >
                      <Badge
                        bg="light"
                        text="primary"
                        className="mb-2"
                        style={{
                          padding: "10px 40px",
                          fontSize: "0.9rem",
                          borderRadius: "0.5rem",
                          display: "block",
                          textAlign: "center",
                        }}
                      >
                        <i className="fa fa-phone me-2"></i>
                        {item.text}
                      </Badge>
                    </a>
                  );
                } else if (
                  item.text === "Copy code" ||
                  item.text === "कोड कॉपी करें"
                ) {
                  return (
                    <div
                      key={index}
                      onClick={() => copyToClipboard(msg.body_text_params)}
                      style={{ cursor: "pointer", textAlign: "center" }}
                    >
                      <Badge
                        bg="light"
                        text="primary"
                        className="mb-2"
                        style={{
                          padding: "10px 40px",
                          fontSize: "0.9rem",
                          borderRadius: "0.5rem",
                          display: "block",
                        }}
                      >
                        <i className="fa fa-copy me-2"></i>
                        {item.text}
                      </Badge>
                    </div>
                  );
                } else if (item.type === "QUICK_REPLY") {
                  return (
                    <Badge
                      bg="light"
                      text="primary"
                      className="mb-2"
                      style={{
                        padding: "10px 40px",
                        fontSize: "0.9rem",
                        borderRadius: "0.5rem",
                        display: "block",
                      }}
                    >
                      {item.text}
                    </Badge>
                  );
                }
                return null;
              })
              : null;
          })()}
        </div>
      </div>
    );
  };

  const hasOutgoingMessage = messages?.some(
    (message) => message.status === "Incoming",
  );

  const fillBgBolor = () => {
    colIndex += 1;
    if (colIndex >= bgColors.length) colIndex = 0;
    return bgColors[colIndex];
  };

  const groupedMessages = messages?.reduce((groups, msg) => {
    const date = moment(msg.createddate).format("YYYY-MM-DD");
    groups[date] = groups[date] || [];
    groups[date].push(msg);
    return groups;
  }, {});

  const getDateLabel = (dateStr) => {
    const messageDate = moment(dateStr);
    if (messageDate.isSame(moment(), "day")) return "Today";
    if (messageDate.isSame(moment().subtract(1, "days"), "day"))
      return "Yesterday";
    return messageDate.format("MMMM D, YYYY");
  };

  const sendQuickReplies = async () => {
    if (userDetail?.blocked) {
      toast.error("Cannot send quick replies to a blocked lead.");
      return;
    }
    if (userDetail.id && userDetail.full_number) {
      setShowHideQuickModal(true);
      setContactData(userDetail);
    } else {
      toast.error("Error: WhatsApp number is required.");
    }
  };

  const handleTagAssignment = async () => {
    try {
      // Fetch fresh lead data before opening modal
      const result = await WhatsAppAPI.fetchLeadById(
        userDetail.lead_id || userDetail.id,
      );
      // const result = await WhatsAppAPI.fetchLeadById(userDetail.id);
      if (result.success) {
        const leadData = {
          id: result.records?.id,
          firstname:
            result.records?.firstname ||
            result.records?.contactname?.split(" ")[0] ||
            "Unknown",
          lastname:
            result.records?.lastname ||
            result.records?.contactname?.split(" ").slice(1).join(" ") ||
            "",
          whatsapp_number: result.records?.full_number,
          country_code: result.records?.country_code,
          email: result.records?.email || "",
          tag_names: result.records?.tag_names || [],
        };
        setSelectedLead(leadData);
        setShowTagModal(true);
      }
    } catch (error) {
      console.error("Error fetching lead data:", error);
      // Fallback to existing data
      const leadData = {
        id: userDetail.id,
        firstname: userDetail.contactname?.split(" ")[0] || "Unknown",
        lastname: userDetail.contactname?.split(" ").slice(1).join(" ") || "",
        whatsapp_number: userDetail.full_number || userDetail.whatsapp_number,
        country_code: userDetail.country_code,
        email: userDetail.email || "",
        tag_names: userDetail.tag_names || [],
      };
      setSelectedLead(leadData);
      setShowTagModal(true);
    }
  };

  return (
    <>
      <Card className="h-100">
        <Card.Header className="p-3">
          <Row className="g-0">
            <Col
              className="text-uppercase"
              lg={7}
              xs={6}
              sm={6}
              style={{ display: "flex", alignItems: "center" }}
            >
              {brokenImages.includes(`img-${userDetail?.id}`) ? (
                <NameInitialsAvatar
                  size="30px"
                  textSize="12px"
                  bgColor={fillBgBolor()}
                  borderWidth="0px"
                  textColor="#fff"
                  name={userDetail.contactname || "Unknown"}
                />
              ) : (
                <img
                  alt=""
                  style={{ height: "30px", width: "30px", objectFit: "cover" }}
                  src={`/${userInfo.tenantcode}/users/${userDetail?.id}`}
                  className="rounded-circle"
                  onError={() =>
                    setBrokenImages((prev) => [
                      ...prev,
                      `img-${userDetail?.id}`,
                    ])
                  }
                  id={`img-${userDetail?.id}`}
                />
              )}

              <span
                className="fw-bold mx-3 mt-1 text-capitalize"
                style={{ fontSize: "15px", color: "white" }}
              >
                {userDetail?.contactname || "Unknown"}{" "}
                <span className="fw-light">
                  {formatPhoneNumber(userDetail?.full_number || "")}
                </span>
              </span>
            </Col>

            <Col
              lg={5}
              xs={3}
              sm={3}
              className="text-end d-flex justify-content-end align-items-center"
            >
              {/* Call Icon with Modal Trigger */}
              {userInfo?.modules?.some((module) => module.url === "calls") && (
                <i
                  className="fa-solid fa-phone mx-3"
                  style={{
                    fontSize: "16px",
                    color: userDetail?.blocked ? "#6c757d" : "white",
                    cursor: userDetail?.blocked ? "not-allowed" : "pointer",
                    pointerEvents: userDetail?.blocked ? "none" : "auto",
                  }}
                  title={userDetail?.blocked ? "Call (Blocked)" : "Call"}
                  onClick={() => {
                    if (userDetail?.blocked) return;
                    setShowCallModal(true);
                  }}
                ></i>
              )}

              {/* Archive/Unarchive Icon */}
              {userDetail?.id && userDetail.full_number && (
                <i
                  className={`fas ${isArchived ? "fa-folder-open" : "fa-archive"} mx-3`}
                  style={{
                    fontSize: "16px",
                    color: isArchived ? "#ffc107" : "white",
                    cursor: "pointer",
                    opacity: isArchiving ? 0.5 : 1,
                  }}
                  title={isArchived ? "Unarchive" : "Archive"}
                  onClick={handleArchiveToggle}
                  disabled={isArchiving}
                >
                  {isArchiving && (
                    <span
                      className="spinner-border spinner-border-sm ms-1"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  )}
                </i>
              )}

              {userInfo?.modules?.some((module) => module.url === "tags") && (
                <i
                  className="fas fa-tags mx-3"
                  style={{
                    fontSize: "16px",
                    color: "white",
                    cursor: "pointer",
                  }}
                  title="Assign Tags"
                  onClick={() => handleTagAssignment()}
                ></i>
              )}

              {/* Dropdown */}
              <Dropdown>
                <Dropdown.Toggle
                  variant="link"
                  className="p-0 text-white text-decoration-none shadow-none"
                  title="Action"
                  style={{ border: "none", opacity: 0.95 }}
                >
                  <i className="fa-solid fa-ellipsis-vertical"></i>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item
                    disabled={!!userDetail?.blocked}
                    onClick={
                      filterData?.recordType === "groups"
                        ? sendBulkTemplateMessage
                        : sendTemplateMessage
                    }
                  >
                    Send Template
                  </Dropdown.Item>
                  {hasQuickModule && (
                    <Dropdown.Item
                      disabled={!!userDetail?.blocked}
                      onClick={sendQuickReplies}
                    >
                      Quick Replies
                    </Dropdown.Item>
                  )}

                  {userInfo?.modules?.some(
                    (module) => module.url === "tags",
                  ) && (
                      <Dropdown.Item onClick={() => handleTagAssignment()}>
                        Assign Tags
                      </Dropdown.Item>
                    )}
                  <Dropdown.Item onClick={onClearButton}>
                    Clear chat
                  </Dropdown.Item>
                  {filterData?.recordType !== "groups" && (
                    <Dropdown.Item onClick={onBlockLeadButton}>
                      Block
                    </Dropdown.Item>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </Col>
          </Row>
          {/* Modal Render */}
          {showCallModal && (
            <CallModal
              show={showCallModal}
              onHide={() => setShowCallModal(false)}
              userDetail={userDetail}
              selectedWhatsAppSetting={selectedWhatsAppSetting}
              socket={socket}
            />
          )}
        </Card.Header>

        {messages?.length > 0 ? (
          <div
            ref={chatWindowRef}
            className="chat-window"
            onScroll={handleChatScroll}
            style={{
              height: "35rem",
              overflowY: "scroll",
              overflowX: "hidden",
              background: "#efe7dd",
            }}
          >
            <Card.Body className="">
              {(loadingOlder || loadingHistoryInitial) && (
                <div className="text-center py-2 small text-muted">
                  {loadingOlder
                    ? "Loading older messages…"
                    : "Loading messages…"}
                </div>
              )}
              <Row className="g-0">
                <Col lg={12} sm={12} xs={12} className="mb-2">
                  {Object.entries(groupedMessages)
                    .sort(([dateA], [dateB]) =>
                      moment(dateA).diff(moment(dateB)),
                    )
                    .map(([date, dateMessages]) => (
                      <div key={date}>
                        <div className="text-center my-1">
                          <span className="px-3 py-1 rounded-pill bg-body-secondary">
                            {getDateLabel(date)}
                          </span>
                        </div>
                        {dateMessages.map((msg, index) => (
                          <div
                            className="conversation"
                            key={
                              msg.message_history_id ??
                              msg.id ??
                              `msg-${date}-${index}`
                            }
                          >
                            <div className="conversation-container">
                              <div
                                className={`message ${msg.status === "Incoming"
                                    ? "received"
                                    : "sent"
                                  }`}
                              >
                                <div>
                                  {msg.status !== "Incoming" && (
                                    <span
                                      className="fw-semibold"
                                      style={{
                                        fontSize: "0.75rem",
                                        color: "rgb(140 51 241)",
                                      }}
                                    >
                                      ~ {msg?.firstname} {msg?.lastname}
                                    </span>
                                  )}

                                  {renderFilePreview(msg)}
                                </div>
                                {msg.buttons && msg.buttons.length > 0 && (
                                  <div className="mb-1 mt-3">
                                    {renderWebsiteAndCall(msg)}
                                  </div>
                                )}
                                {msg?.err_message && (
                                  <span className="text-danger">
                                    {msg?.err_message}
                                  </span>
                                )}

                                <span className="metadata">
                                  <span className="time">
                                    {moment(msg.createddate).format("h:mm A")}
                                    {msg.status !== "Incoming" && (
                                      <span className="ps-1">
                                        {/* ✅ Single tick for 'sent' */}
                                        {msg.delivery_status === "sent" && (
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="15"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                          >
                                            <path
                                              d="M20 6L9 17l-5-5"
                                              stroke="#848b8e"
                                              strokeWidth="2"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                          </svg>
                                        )}

                                        {/* ✅ Double tick for 'delivered' */}
                                        {msg.delivery_status ===
                                          "delivered" && (
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              width="16"
                                              height="15"
                                              viewBox="0 0 24 24"
                                              fill="none"
                                            >
                                              <path
                                                d="M20 6L9 17l-5-5"
                                                stroke="#848b8e"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              />
                                              <path
                                                d="M23 6L12 17l-2-2"
                                                stroke="#848b8e"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              />
                                            </svg>
                                          )}

                                        {/* ✅ Blue double tick for 'read' */}
                                        {msg.delivery_status === "read" && (
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="15"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                          >
                                            <path
                                              d="M20 6L9 17l-5-5"
                                              stroke="#4fc3f7"
                                              strokeWidth="2"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                            <path
                                              d="M23 6L12 17l-2-2"
                                              stroke="#4fc3f7"
                                              strokeWidth="2"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                          </svg>
                                        )}

                                        {/* ❌ Red circle for failed */}
                                        {msg.delivery_status === "failed" && (
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="#ff3b30"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          >
                                            <circle cx="12" cy="12" r="10" />
                                            <line
                                              x1="12"
                                              y1="8"
                                              x2="12"
                                              y2="12"
                                            />
                                            <line
                                              x1="12"
                                              y1="16"
                                              x2="12"
                                              y2="16"
                                            />
                                          </svg>
                                        )}
                                      </span>
                                    )}
                                  </span>
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                </Col>
              </Row>
            </Card.Body>
          </div>
        ) : (
          <div style={{ height: "100%", background: "#efe7dd" }}>
            <Row className="g-0">
              <Col lg={12} sm={12} xs={12} className="p-4">
                <span style={{ fontSize: "15px" }}>
                  There are no chats available here. Sending your first message
                  is necessary because you haven't chatted before. Send your
                  initial message using the Send button below.
                </span>
              </Col>
              <Col lg={12} sm={12} xs={12} className="text-center p-2">
                {filterData?.recordType !== "groups" ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-light mx-1"
                    disabled={!!userDetail?.blocked}
                    onClick={sendTemplateMessage}
                  >
                    Send Message
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-sm btn-light mx-1"
                    onClick={sendBulkTemplateMessage}
                  >
                    Send Bulk Message
                  </button>
                )}
              </Col>

              <Col lg={12} sm={12} xs={12} className="p-4 mt-5 text-center">
                <p>
                  <i
                    className="fa-brands fa-whatsapp"
                    style={{ fontSize: "30px", color: "rgb(119 110 102)" }}
                  ></i>
                </p>
                <p>WhatsApp for Windows</p>
                <p>Send and recieve messages</p>
              </Col>
            </Row>
          </div>
        )}

        {/* {hasOutgoingMessage ? ( */}
        <Card.Footer>
          <Row className="g-0 mt-2">
            <Col lg={11} sm={8} xs={8}>
              <div
                className="input-focus-class"
                style={{
                  position: "relative",
                  display: "inline-block",
                  width: "100%",
                }}
              >
                <textarea
                  placeholder={userDetail?.blocked ? "This lead is blocked. Unblock to send messages." : "Type message here..."}
                  disabled={!!userDetail?.blocked}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (userDetail?.blocked) return;
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (input.trim() === "") return;
                      handleSubmit();
                      setInput("");
                    }
                  }}
                  style={{
                    height: "20px",
                    width: "100%",
                    padding: "10px 40px 10px 10px",
                    borderRadius: "20px",
                    border: "1px solid #ccc",
                    resize: "none",
                    minHeight: "40px",
                    overflow: "hidden",
                    outline: "none",
                  }}
                />
                {!userDetail?.blocked && (
                  <i
                    className="fa-solid fa-paperclip"
                    style={{
                      position: "absolute",
                      right: "15px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: "18px",
                      color: "#999fab",
                      cursor: "pointer",
                    }}
                    onClick={onUploadFile}
                  ></i>
                )}
              </div>
            </Col>
            <Col lg={1} xs={4} sm={4} className="text-center">
              <button
                type="button"
                className="btn btn-outline-secondary mx-1 mt-1"
                disabled={!!userDetail?.blocked}
                onClick={handleSubmit}
              >
                <i className="fa fa-paper-plane"></i>
              </button>
            </Col>
          </Row>
        </Card.Footer>
        {/* ) : (
          <Card.Footer>
            <Row className="g-0 mt-3">
              <Col lg={6} sm={12} xs={12} className="mt-1">
                <p>
                  <b>There are no incoming chats available here.</b>
                </p>
              </Col>
            </Row>
          </Card.Footer>
        )} */}
      </Card>

      {showHideModal && (
        <TemplateModal
          show={showHideModal}
          onHide={() => setShowHideModal(false)}
          contactData={contactData}
          refreshData={refreshData}
          filterData={filterData}
          selectedWhatsAppSetting={selectedWhatsAppSetting}
        />
      )}

      {fileModalShowHide && (
        <SendFileModal
          show={fileModalShowHide}
          onHide={() => setFileModalShowHide(false)}
          refreshImageData={refreshImageData}
          parentData={userDetail}
          filterData={filterData}
          selectedWhatsAppSetting={selectedWhatsAppSetting}
        />
      )}

      {showHideBulkModal && (
        <MessageTemplateModal
          show={showHideBulkModal}
          onHide={() => setShowHideBulkModal(false)}
          contactData={groupData}
          refreshData={refreshData}
          filterData={filterData}
          selectedWhatsAppSetting={selectedWhatsAppSetting}
        />
      )}
      {showHideQuickModal && (
        <QuickRepliesTemplateModel
          show={showHideQuickModal}
          onHide={() => setShowHideQuickModal(false)}
          contactData={contactData}
          refreshData={refreshData}
          filterData={filterData}
          selectedWhatsAppSetting={selectedWhatsAppSetting}
        />
      )}

      {showTagModal && selectedLead && (
        <TagAssignmentModal
          show={showTagModal}
          onHide={() => {
            setShowTagModal(false);
            setSelectedLead(null);
          }}
          lead={selectedLead}
          onTagUpdate={(newTags) => {
            // You might want to update a global state or context here
          }}
        // refreshLeadData={() => {
        //   // Refresh local data
        //   getMessageHistotyRecords(selectedWhatsAppSetting);
        // }}
        />
      )}
    </>
  );
};

export default WhatsAppChat;
